import * as IconSolid from '@heroicons/react/24/solid'
import { Fragment, useEffect, useState } from 'react'
import moment from 'moment'
import { Menu, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { generateCalendarDates } from 'Common/Utility/generator';
import { cn } from 'Common/Utility/class-name';

const today = moment();
const todayString = today.format('YYYY-MM-DD')

export function useHeadlessCalender({ initialCursorDate = moment(), onChangeDays = () => null }) {
    const [cursorDate, setCursorDate] = useState(initialCursorDate)
    const [days, setDays] = useState([])

    useEffect(() => {
        generateCalendarDates(cursorDate.toDate()).then(response => {
            const changedDays = response.map(day => ({
                date: day.iso,
                isCurrentMonth: day.type === 'current',
                isToday: day.iso === todayString
            }))
            setDays(changedDays)
            onChangeDays(changedDays)
        })
    }, [cursorDate])

    return { days, cursorDate, setCursorDate }
}

export default function CalenderView({ title = null, initialCursorDate = moment(), selectedDate = null, onSelectedDate = null, selectableDateOn = 'container', renderItem, renderItemMobile, additionalMenu = null, onChangeCursor = () => null, onChangeDays = () => null }) {
    const { days, setCursorDate, cursorDate } = useHeadlessCalender({ initialCursorDate, onChangeDays })
    return (
        <div className="lg:flex lg:h-full lg:flex-col">
            <header className="relative z-20 flex items-center justify-between border-b border-gray-200 py-4 px-6 lg:flex-none">
                <div>
                    <time dateTime="2022-01" className="text-gray-900 text-3xl font-extrabold tracking-tight mt-2">
                        <span className="text-indigo-500">{cursorDate.format('MMMM')}</span>, {cursorDate.format('YYYY')}
                    </time>
                    {!title ? null :
                        <h2 className=" text-gray-500 text-xl -mt-2 font-medium space-x-2 whitespace-nowrap mb-3">
                            {title}
                        </h2>
                    }
                </div>
                <div className="flex items-center">
                    <div className="flex items-center rounded-md shadow-sm md:items-stretch">
                        <button
                            type="button"
                            onClick={() => setCursorDate(cursorDate.clone().subtract(1, 'month'))}
                            className="flex items-center justify-center rounded-l-md border border-r-0 border-gray-300 bg-white py-2 pl-3 pr-4 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:px-2 md:hover:bg-gray-50"
                        >
                            <span className="sr-only">Previous month</span>
                            <IconSolid.ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                const newCursor = moment()
                                setCursorDate(newCursor)
                                onChangeCursor(newCursor)
                            }}
                            className="hidden border-t border-b border-gray-300 bg-white px-3.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 focus:relative md:block"
                        >
                            Hari ini
                        </button>
                        <span className="relative -mx-px h-5 w-px bg-gray-300 md:hidden" />
                        <button
                            type="button"
                            onClick={() => {
                                const newCursor = cursorDate.clone().add(1, 'month')
                                setCursorDate(newCursor)
                                onChangeCursor(newCursor)
                            }}
                            className="flex items-center justify-center rounded-r-md border border-l-0 border-gray-300 bg-white py-2 pl-4 pr-3 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:px-2 md:hover:bg-gray-50"
                        >
                            <span className="sr-only">Next month</span>
                            <IconSolid.ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                    </div>
                    <div className="hidden md:ml-4 md:flex md:items-center gap-3.5">
                        <Menu as="div" className="relative hidden">
                            <Menu.Button
                                type="button"
                                className="flex items-center rounded-md border border-gray-300 bg-white py-2 pl-3 pr-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                            >
                                Month view
                                <IconSolid.ChevronDownIcon className="ml-2 h-5 w-5 text-gray-400" aria-hidden="true" />
                            </Menu.Button>

                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                            >
                                <Menu.Items className="focus:outline-none absolute right-0 mt-3 w-36 origin-top-right overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                                    <div className="py-1">
                                        <Menu.Item>
                                            {({ active }) => (
                                                <a
                                                    href="#"
                                                    className={classNames(
                                                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                                        'block px-4 py-2 text-sm'
                                                    )}
                                                >
                                                    Day view
                                                </a>
                                            )}
                                        </Menu.Item>
                                        <Menu.Item>
                                            {({ active }) => (
                                                <a
                                                    href="#"
                                                    className={classNames(
                                                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                                        'block px-4 py-2 text-sm'
                                                    )}
                                                >
                                                    Week view
                                                </a>
                                            )}
                                        </Menu.Item>
                                        <Menu.Item>
                                            {({ active }) => (
                                                <a
                                                    href="#"
                                                    className={classNames(
                                                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                                        'block px-4 py-2 text-sm'
                                                    )}
                                                >
                                                    Month view
                                                </a>
                                            )}
                                        </Menu.Item>
                                        <Menu.Item>
                                            {({ active }) => (
                                                <a
                                                    href="#"
                                                    className={classNames(
                                                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                                        'block px-4 py-2 text-sm'
                                                    )}
                                                >
                                                    Year view
                                                </a>
                                            )}
                                        </Menu.Item>
                                    </div>
                                </Menu.Items>
                            </Transition>
                        </Menu>
                        <div className=" h-6 w-px bg-gray-300" />

                        {additionalMenu}
                    </div>
                    <Menu as="div" className="relative ml-6 md:hidden">
                        <Menu.Button className="-mx-2 flex items-center rounded-full border border-transparent p-2 text-gray-400 hover:text-gray-500">
                            <span className="sr-only">Open menu</span>
                            <IconSolid.EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
                        </Menu.Button>

                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                        >
                            <Menu.Items className="focus:outline-none absolute right-0 mt-3 w-36 origin-top-right divide-y divide-gray-100 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                                <div className="py-1">
                                    <Menu.Item>
                                        {({ active }) => (
                                            <a
                                                href="#"
                                                className={classNames(
                                                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                                    'block px-4 py-2 text-sm'
                                                )}
                                            >
                                                Create event
                                            </a>
                                        )}
                                    </Menu.Item>
                                </div>
                                <div className="py-1">
                                    <Menu.Item>
                                        {({ active }) => (
                                            <a
                                                href="#"
                                                className={classNames(
                                                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                                    'block px-4 py-2 text-sm'
                                                )}
                                            >
                                                Go to today
                                            </a>
                                        )}
                                    </Menu.Item>
                                </div>
                                <div className="py-1">
                                    <Menu.Item>
                                        {({ active }) => (
                                            <a
                                                href="#"
                                                className={classNames(
                                                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                                    'block px-4 py-2 text-sm'
                                                )}
                                            >
                                                Day view
                                            </a>
                                        )}
                                    </Menu.Item>
                                    <Menu.Item>
                                        {({ active }) => (
                                            <a
                                                href="#"
                                                className={classNames(
                                                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                                    'block px-4 py-2 text-sm'
                                                )}
                                            >
                                                Week view
                                            </a>
                                        )}
                                    </Menu.Item>
                                    <Menu.Item>
                                        {({ active }) => (
                                            <a
                                                href="#"
                                                className={classNames(
                                                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                                    'block px-4 py-2 text-sm'
                                                )}
                                            >
                                                Month view
                                            </a>
                                        )}
                                    </Menu.Item>
                                    <Menu.Item>
                                        {({ active }) => (
                                            <a
                                                href="#"
                                                className={classNames(
                                                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                                    'block px-4 py-2 text-sm'
                                                )}
                                            >
                                                Year view
                                            </a>
                                        )}
                                    </Menu.Item>
                                </div>
                            </Menu.Items>
                        </Transition>
                    </Menu>
                </div>
            </header>
            <div className="shadow ring-1 ring-black ring-opacity-5 lg:flex lg:flex-auto lg:flex-col">
                <div className="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs font-semibold leading-6 text-gray-700 lg:flex-none">
                    <div className="bg-white py-2">
                        M<span className="sr-only sm:not-sr-only">on</span>
                    </div>
                    <div className="bg-white py-2">
                        T<span className="sr-only sm:not-sr-only">ue</span>
                    </div>
                    <div className="bg-white py-2">
                        W<span className="sr-only sm:not-sr-only">ed</span>
                    </div>
                    <div className="bg-white py-2">
                        T<span className="sr-only sm:not-sr-only">hu</span>
                    </div>
                    <div className="bg-white py-2">
                        F<span className="sr-only sm:not-sr-only">ri</span>
                    </div>
                    <div className="bg-white py-2">
                        S<span className="sr-only sm:not-sr-only">at</span>
                    </div>
                    <div className="bg-white py-2">
                        S<span className="sr-only sm:not-sr-only">un</span>
                    </div>
                </div>
                <div className="flex bg-gray-200 text-xs leading-6 text-gray-700 lg:flex-auto">
                    <div className="hidden w-full lg:grid lg:grid-cols-7 lg:grid-rows-6 lg:gap-px">
                        {days.map((day) => (
                            <div
                                key={day.date}
                                onClick={!onSelectedDate ? null : (selectableDateOn === 'container' ? () => onSelectedDate(day) : null)}
                                className={cn(
                                    day.isCurrentMonth ? 'bg-white' : 'bg-gray-50 text-gray-500',
                                    'relative py-2 px-3 min-h-[100px]',
                                    !onSelectedDate ? null : (selectableDateOn === 'container' ? 'cursor-pointer' : null)
                                )}
                            >
                                <time
                                    dateTime={day.date}
                                    onClick={!onSelectedDate ? null : (selectableDateOn === 'date-label' ? () => onSelectedDate(day) : null)}
                                    className={cn(
                                        "rounded-full h-6 w-6 flex items-center justify-center",
                                        day.isToday
                                            ? 'flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 font-semibold text-white'
                                            : undefined,
                                        !onSelectedDate ? null : (selectableDateOn === 'date-label' ? 'cursor-pointer' : null),
                                        selectedDate === day.date ? 'ring-2 ring-indigo-200' : null
                                    )}
                                >
                                    {day.date.split('-').pop().replace(/^0/, '')}
                                </time>

                                {renderItem(day)}
                            </div>
                        ))}
                    </div>
                    <div className="isolate grid w-full grid-cols-7 grid-rows-6 gap-px lg:hidden">
                        {days.map((day) => {
                            return (
                                <button
                                    key={day.date}
                                    type="button"
                                    onClick={!onSelectedDate ? null : (selectableDateOn === 'container' ? () => onSelectedDate(day) : null)}
                                    className={cn(
                                        day.isCurrentMonth ? 'bg-white' : 'bg-gray-50',
                                        (day.isSelected || day.isToday) && 'font-semibold',
                                        day.isSelected && 'text-white',
                                        !day.isSelected && day.isToday && 'text-indigo-600',
                                        !day.isSelected && day.isCurrentMonth && !day.isToday && 'text-gray-900',
                                        !day.isSelected && !day.isCurrentMonth && !day.isToday && 'text-gray-500',
                                        'flex h-14 flex-col py-2 px-3 hover:bg-gray-100 focus:z-10',
                                        !onSelectedDate ? null : (selectableDateOn === 'container' ? 'cursor-pointer' : null)
                                    )}
                                >
                                    <time
                                        dateTime={day.date}
                                        onClick={!onSelectedDate ? null : (selectableDateOn === 'date-label' ? () => onSelectedDate(day) : null)}
                                        className={cn(
                                            day.isSelected && 'flex h-6 w-6 items-center justify-center rounded-full',
                                            day.isSelected && day.isToday && 'bg-indigo-600',
                                            day.isSelected && !day.isToday && 'bg-gray-900',
                                            'ml-auto',
                                            !onSelectedDate ? null : (selectableDateOn === 'date-label' ? 'cursor-pointer' : null),
                                            selectedDate === day.date ? 'ring-2 ring-indigo-200' : null
                                        )}
                                    >
                                        {day.date.split('-').pop().replace(/^0/, '')}
                                    </time>
                                    {renderItemMobile(day)}
                                </button>
                            )
                        })}
                    </div>
                </div>
            </div>
        </div>
    )
}